// src/utils/request.ts
import { authUtils } from "@/lib/auth";
import type { ApiResponse } from "@/types/api.types";
import { TokenResponse } from "@/types/auth.types";

// 请求配置接口
interface RequestConfig extends Omit<RequestInit, "headers"> {
  auth?: boolean; // 是否需要认证
  retry?: boolean; // 是否在token刷新后重试
  headers?: Record<string, string>;
  responseType?: "json" | "blob" | "text"; // 添加响应类型选项
}

// 错误类型
export class ApiError extends Error {
  constructor(
    public status: string,
    public statusText: string,
    public data?: any
  ) {
    super(statusText);
    this.name = "ApiError";
  }
}

// 用于存储等待中的请求
let refreshPromise: Promise<boolean> | null = null;
const waitingQueue: (() => void)[] = [];

// Define default config
const DEFAULT_CONFIG: RequestConfig = {
  auth: true,
  retry: true,
  headers: {
    "Content-Type": "application/json",
  },
};

export const request = async <T = any>(
  url: string,
  config: RequestConfig = {}
): Promise<T> => {
  // Merge default config with provided config
  const finalConfig = {
    ...DEFAULT_CONFIG,
    ...config,
    headers: {
      ...DEFAULT_CONFIG.headers,
      ...config.headers,
    },
  };

  const {
    auth = true,
    retry = true,
    headers = {},
    responseType = "json",
    ...rest
  } = finalConfig;

  // 构建请求头
  const requestHeaders: Record<string, string> = {
    "Content-Type": "application/json",
    ...headers,
  };

  // 构建完整的URL
  const baseURL = import.meta.env.VITE_API_BASE_URL || "";
  const fullUrl = `${baseURL}${url}`;

  // 如果需要认证，添加token
  if (auth) {
    const tokens = authUtils.getTokens();
    if (tokens?.accessToken) {
      requestHeaders["Authorization"] = `Bearer ${tokens.accessToken}`;
    }
  }

  try {
    const response = await fetch(fullUrl, {
      ...rest,
      headers: requestHeaders,
    });

    // 处理401错误（token过期）
    if (response.status === 401 && auth && retry) {
      const newToken = await handleTokenRefresh();
      if (newToken) {
        // 使用新token重试请求
        return request<T>(url, {
          ...config,
          retry: false, // 防止无限循环
        });
      }
      // 如果刷新失败，跳转到登录页
      handleAuthError();
      throw new ApiError("UNAUTHORIZED", "Unauthorized");
    }

    // 根据responseType处理不同类型的响应
    if (responseType === "blob") {
      if (!response.ok) {
        throw new ApiError(String(response.status), response.statusText);
      }
      return response.blob() as Promise<T>;
    } else if (responseType === "text") {
      if (!response.ok) {
        throw new ApiError(String(response.status), response.statusText);
      }
      return response.text() as Promise<T>;
    } else {
      // 默认处理JSON响应
      const jsonResponse: ApiResponse<T> = await response.json();

      if (jsonResponse.code !== "SUCCESS") {
        throw new ApiError(
          jsonResponse.code,
          jsonResponse.message,
          jsonResponse.data
        );
      }

      // 返回实际的数据部分
      return jsonResponse.data;
    }
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError("SERVICE_UNAVAILABLE", "Network Error");
  }
};

// 处理token刷新
async function handleTokenRefresh(): Promise<boolean> {
  // 如果已经在刷新中，加入等待队列
  if (refreshPromise) {
    return new Promise((resolve) => {
      waitingQueue.push(() => resolve(true));
    });
  }

  const tokens = authUtils.getTokens();
  if (!tokens?.refreshToken) {
    return false;
  }

  console.log("开始刷新token\n");

  // 开始刷新token
  refreshPromise = (async () => {
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/refresh`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokens.refreshToken}`,
          },
          body: JSON.stringify({}),
        }
      );

      console.log("刷新token完成：\n");
      console.log(response);

      if (!response.ok) {
        throw new Error("Token refresh failed");
      }

      const jsonResponse: ApiResponse<TokenResponse> = await response.json();

      if (jsonResponse.code !== "SUCCESS") {
        throw new Error(jsonResponse.message);
      }

      // 转换响应数据格式以匹配 authUtils 期望的格式
      const newTokens = {
        accessToken: jsonResponse.data.access_token,
        refreshToken: jsonResponse.data.refresh_token,
      };

      authUtils.setTokens(newTokens);

      waitingQueue.forEach((resolve) => resolve());
      waitingQueue.length = 0;

      return true;
    } catch (error) {
      console.error("Token refresh failed:", error);
      authUtils.clearTokens();
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// 处理认证错误
function handleAuthError() {
  authUtils.clearTokens();
  // 可以使用你的路由管理器来处理重定向
  window.location.href = "/login";
}

// 请求方法简写
export const http = {
  get: <T = any>(
    url: string,
    params?: Record<string, any>,
    config?: Partial<RequestConfig>
  ) => {
    // Convert params to URL search params
    const queryParams = params
      ? `?${new URLSearchParams(
          Object.entries(params).reduce((acc, [key, value]) => {
            if (value !== undefined && value !== null) {
              acc[key] = String(value);
            }
            return acc;
          }, {} as Record<string, string>)
        ).toString()}`
      : "";

    return request<T>(`${url}${queryParams}`, {
      ...DEFAULT_CONFIG,
      ...config,
      method: "GET",
    });
  },

  post: <T = any>(url: string, data?: any, config?: Partial<RequestConfig>) =>
    request<T>(url, {
      ...DEFAULT_CONFIG,
      ...config,
      method: "POST",
      body: JSON.stringify(data),
    }),

  put: <T = any>(url: string, data?: any, config?: Partial<RequestConfig>) =>
    request<T>(url, {
      ...DEFAULT_CONFIG,
      ...config,
      method: "PUT",
      body: JSON.stringify(data),
    }),

  delete: <T = any>(url: string, config?: Partial<RequestConfig>) =>
    request<T>(url, {
      ...DEFAULT_CONFIG,
      ...config,
      method: "DELETE",
    }),

  patch: <T = any>(url: string, data?: any, config?: Partial<RequestConfig>) =>
    request<T>(url, {
      ...DEFAULT_CONFIG,
      ...config,
      method: "PATCH",
      body: JSON.stringify(data),
    }),
};
